import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddAccountComponent } from '../../add-account/containers/add-account.component';
import { AccountOrderingOptions, AccountRegisterOptions } from '../models/account-register-options';
import { AccountRegisterListItem, AccountRegisterResults } from '../models/account-register-results.model';
import { AccountsFilterOption } from '../models/accounts-filter-option';
import { GetAccounts, GetAccounts_accountsConnection_items } from '../queries/generated/GetAccounts';
import { AccountRegisterApiService } from '../services/account-register-api.service';
import { AccountRegisterSessionStorageService } from '../services/account-register-session-storage.service';
import {
   accountRegisterAdapterActions,
   createAccount,
   setOrderingOptionsFromSessionStorage,
} from './account-register.actions';
import {
   AccountRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectQuickFilters,
} from './account-register.reducer';
import { selectFilters } from './account-register.selectors';
@Injectable()
export class AccountRegisterEffects {
   addAccount$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createAccount),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddAccountComponent, {
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadAccountList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(accountRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickFilters, orderingOptions]) => {
            const sessionOrderingOptions = this.accountRegisterSessionStorageService.getAccountOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }

            return this.loadNewData(quickFilters, filters, orderingOptions);
         })
      );
   });

   loadAccountPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(accountRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickFilters, endCursor, orderingOptions]) => {
            const sessionOrderingOptions = this.accountRegisterSessionStorageService.getAccountOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewPage(endCursor, quickFilters, filters, orderingOptions);
         })
      );
   });

   loadAccountIdPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(accountRegisterAdapterActions.loadIdPage),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickFilters, endCursor, orderingOptions]) => {
            const sessionOrderingOptions = this.accountRegisterSessionStorageService.getAccountOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewAcccountIdPage(endCursor, quickFilters, filters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(accountRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(quickFilters, filters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(accountRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectFilters))
         ),
         switchMap(([action, quickfilters, orderingOptions, filters]) => {
            this.accountRegisterSessionStorageService.setAccountOrderingOptions(orderingOptions);
            return this.loadNewData(quickfilters, filters, orderingOptions);
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<AccountRegisterState>,
      private accountRegisterApiService: AccountRegisterApiService,
      private dialogService: LcsDialogService,
      private accountRegisterSessionStorageService: AccountRegisterSessionStorageService
   ) {}

   private convertGetAccountsResultToLoadSuccessProps(getAccounts: GetAccounts): AccountRegisterResults {
      return {
         endCursor: getAccounts.accountsConnection.pageInfo.endCursor,
         hasNextPage: getAccounts.accountsConnection.pageInfo.hasNextPage,
         items: getAccounts.accountsConnection.items.map(this.convertAccountRegisterListItem),
         totalCount: getAccounts.accountsConnection.totalCount,
      };
   }

   private convertAccountRegisterListItem(
      getAccountItem: GetAccounts_accountsConnection_items
   ): AccountRegisterListItem {
      return {
         accountId: getAccountItem.accountId,
         lcsAccountNumber: getAccountItem.lcsAccountNumber,
         name: getAccountItem.name,
         shortName: getAccountItem.shortName,
         isActive:
            (getAccountItem.activeStartDate == null ||
               Date.parse(getAccountItem.activeStartDate) < Date.parse(Date())) &&
            (getAccountItem.activeEndDate == null || Date.parse(Date()) < Date.parse(getAccountItem.activeEndDate)),
         pstnLineLimit: getAccountItem.pstnLineLimit,
         extensionCount: getAccountItem.extensionsConnection.items.filter((extension) => !extension.isArchived).length,
         phoneNumberCount: getAccountItem.phoneNumbersConnection.items.filter((phoneNumber) => !phoneNumber.isArchived)
            .length,
         logLevelId: getAccountItem.logLevelId,
         logLevelChanged: getAccountItem.logLevelChanged,
      };
   }

   private loadNewData(
      quickFilters: AccountRegisterOptions | null = null,
      filters: AccountsFilterOption[] | null = null,
      orderingOptions: AccountOrderingOptions[] | null = null
   ) {
      return this.accountRegisterApiService.getAccounts(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return accountRegisterAdapterActions.loadSuccess(
               this.convertGetAccountsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(accountRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      quickFilters: AccountRegisterOptions | null = null,
      filters: AccountsFilterOption[] | null = null,
      orderingOptions: AccountOrderingOptions[] | null = null
   ) {
      return this.accountRegisterApiService.getAccounts(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return accountRegisterAdapterActions.loadPageSuccess(
               this.convertGetAccountsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(accountRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }

   private loadNewAcccountIdPage(
      endCursor: string | null,
      quickFilters: AccountRegisterOptions | null = null,
      filters: AccountsFilterOption[] | null = null,
      orderingOptions: AccountOrderingOptions[] | null = null
   ) {
      return this.accountRegisterApiService.getAccountIds(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return accountRegisterAdapterActions.loadIdPageSuccess({
               ids: result.data.accountsConnection.items.map((i) => i.accountId),
            });
         }),
         catchError((error) => {
            return of(accountRegisterAdapterActions.loadIdPageFailure(error));
         })
      );
   }
}
